import { useMemo } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import type { FallbackProps } from 'react-error-boundary';
import { createAppTheme, ThemeMode } from '../../theme';
import ErrorFallback from './error-fallback';

/**
 * Resolve the persisted light/dark choice directly. The top-level boundary can
 * render OUTSIDE ThemeModeProvider, so we can't rely on its context here.
 */
const resolveMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem('theme');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* localStorage unavailable */
  }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return 'light';
};

/**
 * Last-resort, full-page fallback for the app-level and route-shell boundaries.
 * It brings its own theme (honouring the persisted mode so dark mode still
 * looks right) and recovers with a hard reload, which is the only reliable
 * recovery once a provider or the layout shell itself has failed.
 */
const MainErrorFallback = ({ error }: Partial<FallbackProps>) => {
  const theme = useMemo(() => createAppTheme(resolveMode()), []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorFallback
        variant='page'
        error={error}
        onRetry={() => window.location.reload()}
        onGoHome={() => window.location.assign('/')}
      />
    </ThemeProvider>
  );
};

export default MainErrorFallback;
